import { ComputedRef, Ref, computed, readonly, ref } from 'vue';
import { Ticker, MarketCarpetDataResponse, ApiError, fetchMarketCarpetData } from '@/ts/fetchMarketCarpetData';
import { Calculator, CalculatedValues } from '@/ts/map/calculationFunctions';
import { PERIOD_MAP, PeriodKey } from '@/ts/composables/usePeriod';

export interface LeafNode {
  id: number;
  symbol: string;
  name: string;
  marketCap: number | null;
  lastPrice: number;
  calculatedValues: CalculatedValues;
}

export interface ParentNode {
  id: number;
  name: string;
  children: (ParentNode | LeafNode)[];
}

type TreeNodeRef = Ref<ParentNode | LeafNode | null> | ComputedRef<ParentNode | LeafNode | null>;

const DEFAULT_AUTO_REFRESH = true;

// scanengine servlet's V3 carpet API reason codes for errors that typically
// resolve on their own (server restart window, momentary DB outage). Worth
// a single retry before giving up.
const TRANSIENT_ERROR_REASONS = new Set(['REPORT_NOT_READY', 'DATABASE_ERROR']);
const TRANSIENT_RETRY_DELAY_MS = 5000;

// Collects all leaf nodes from a tree via stack-based DFS. Traversal order is
// not significant -- consumers either aggregate (totals, counts) or sort results.
function collectLeafNodes(node: TreeNodeRef): ComputedRef<LeafNode[]> {
  return computed(() => {
    if (!node.value)
      return [];

    const nodes: LeafNode[] = [];
    const traverseNodes: (ParentNode | LeafNode)[] = [node.value];

    while (traverseNodes.length > 0) {
      const current = traverseNodes.pop()!;
      if ('children' in current) {
        traverseNodes.push(...current.children);
      } else {
        nodes.push(current);
      }
    }

    return nodes;
  });
}

// Factory for creating instance-scoped market carpet data state
export function createMarketCarpetData(
  dependencies: {
    calculator: ComputedRef<Calculator>;
    periodKey: Ref<PeriodKey>;
  },
  options?: { autoRefresh?: boolean }
) {
  const rawData = ref<MarketCarpetDataResponse | null>(null);
  const dataState = ref<'loaded' | 'loading' | 'error'>('loading');
  const zoomedInName = ref<string | null>(null);
  const isAutoRefreshEnabled = ref(options?.autoRefresh ?? DEFAULT_AUTO_REFRESH);

  const dates = computed(() => {
    if (!rawData.value)
      return null;
    return rawData.value.dates.map(date => new Date(date));
  });

  const lastUpdated = computed(() => {
    if (!rawData.value)
      return null;
    return rawData.value.lastUpdated;
  });

  const yearToDatePeriod = computed(() => {
    const currentYear = new Date().getFullYear();
    const periodIndex = dates.value?.findIndex(date => date.getFullYear() === currentYear - 1);
    return periodIndex === -1 || periodIndex === undefined ? null : periodIndex;
  });

  const period = computed(() => {
    if (dependencies.periodKey.value === 'yeartodate')
      return yearToDatePeriod.value ?? 2;
    return PERIOD_MAP[dependencies.periodKey.value];
  });

  let nextNodeId = 0;

  function createMapNode(name: string): ParentNode;
  function createMapNode(name: string, ticker: Ticker): LeafNode;
  function createMapNode(name: string, ticker?: Ticker): LeafNode | ParentNode {
    if (ticker) {
      return {
        id: nextNodeId++,
        symbol: ticker.symbol,
        name: ticker.name,
        marketCap: ticker.marketCap,
        lastPrice: ticker.prices[0],
        calculatedValues: dependencies.calculator.value.getCalculations(ticker, period.value)
      };
    }
    return { id: nextNodeId++, name, children: [] };
  }

  const mapRoot = computed<ParentNode | LeafNode | null>(() => {
    if (!rawData.value)
      return null;

    nextNodeId = 0;
    const data = rawData.value;
    const parent = createMapNode(data.title);

    parent.children = data.sectors.map<ParentNode>(sector => {
      const sectorNode = createMapNode(sector.name);
      const tickers = sector.tickers.map<LeafNode>(ticker => {
        return createMapNode(ticker.name, ticker);
      });
      sectorNode.children = tickers;
      return sectorNode;
    });

    if (parent.children.length === 1) // TODO: replicate the name mapping stuff
      return parent.children[0];

    return parent;
  });

  const currentNode = computed(() => {
    if (!mapRoot.value)
      return null;
    if (!zoomedInName.value)
      return mapRoot.value;

    const stack = [mapRoot.value];
    while (stack.length > 0) {
      const node = stack.pop()!;
      if (node.name === zoomedInName.value)
        return node;
      if ('children' in node)
        stack.push(...node.children);
    }
    return mapRoot.value;
  });

  const isZoomed = computed(() => {
    return currentNode.value?.name !== mapRoot.value?.name;
  });

  const leafNodes = collectLeafNodes(mapRoot);
  const currentNodeLeafNodes = collectLeafNodes(currentNode);

  async function fetchData(scanCommand: string, shouldDisplayLoading = true) {
    if (shouldDisplayLoading)
      dataState.value = 'loading';

    try {
      rawData.value = await fetchMarketCarpetData(scanCommand);
      dataState.value = 'loaded';
    } catch (error) {
      // Transient scanengine servlet's V3 carpet API errors (server
      // restart, DB outage) often resolve within seconds — retry
      // once before showing error state on initial loads.
      if (shouldDisplayLoading && error instanceof ApiError && TRANSIENT_ERROR_REASONS.has(error.reason)) {
        try {
          await new Promise(resolve => setTimeout(resolve, TRANSIENT_RETRY_DELAY_MS));
          rawData.value = await fetchMarketCarpetData(scanCommand);
          dataState.value = 'loaded';
          return;
        } catch {
          // Retry failed — fall through to error state
        }
      }

      // Background refreshes silently keep existing data on failure rather
      // than replacing a valid carpet with an error screen.
      if (!shouldDisplayLoading && rawData.value !== null)
        return;

      rawData.value = null;
      dataState.value = 'error';
    }
  }

  function zoomIn(node: ParentNode) {
    zoomedInName.value = node.name; // important in order to retain the zoomed in state after refresh
  }

  function zoomOut() {
    zoomedInName.value = null;
  }

  // rawData and dataState are readonly because they should only be mutated
  // through fetchData() which enforces valid state transitions.
  // isAutoRefreshEnabled is intentionally writable -- the UI, URL sync, and
  // custom element attribute watchers all need to toggle it directly.
  return {
    rawData: readonly(rawData),
    dataState: readonly(dataState),
    mapRoot,
    currentNode,
    leafNodes,
    currentNodeLeafNodes,
    dates,
    lastUpdated,
    period,
    isAutoRefreshEnabled,
    isZoomed,
    zoomedInName,
    fetchData,
    zoomIn,
    zoomOut
  };
}

export {
  DEFAULT_AUTO_REFRESH
};
